import React from "react";
import { t, Trans } from "@lingui/macro";
import { psidValidationEnv } from "../../constants";
import { Alert } from "../../../../../UI/Alert/Alert";
import { useDispatch, useSelector } from "react-redux";
import { Loader } from "../../../../../UI/Loader/Loader";
import { Button } from "../../../../../UI/Button/Button";
import { selectDetailItemId } from "../../../../../../model/dashboardEdmProducts/selectors";
import { getEdmProductItemById } from "../../../../../../model/dashboardEdmProducts/dEdmProductsAPI";
import { IdProductsStateDataSingleItem } from "../../../../../../model/dashboardEdmProducts/dEdmProductsReducer";
import { dEdmProductsSetDetailItemAC } from "../../../../../../model/dashboardEdmProducts/dEdmProductsActionCreators";
import {
  TagIcon,
  CashIcon,
  PencilIcon,
  BookmarkIcon,
  ReceiptTaxIcon,
  ExclamationCircleIcon,
} from "@heroicons/react/outline";

export const PopupDetails: React.FC<{
  setOpenPopup: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({ setOpenPopup }) => {
  const dispatch = useDispatch();
  const detailItemId = useSelector(selectDetailItemId);
  const [hasError, setError] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [dataObject, setData] = React.useState<
    IdProductsStateDataSingleItem | undefined
  >(undefined);

  React.useEffect(() => {
    if (detailItemId) {
      getEdmProductItemById(detailItemId)
        .then((res) => {
          if (res.status !== 200) {
            setError(true);
            setLoading(false);
          } else {
            setLoading(false);

            setData(res.data);
          }
        })
        .catch(() => {
          setError(true);
          setLoading(false);
        });
    }

    return () => {
      dispatch(dEdmProductsSetDetailItemAC(undefined));
    };
  }, [detailItemId, dispatch]);

  if (loading) {
    return <Loader />;
  }

  if (hasError || !dataObject) {
    return <Alert type="error" title={<Trans>Have no data.</Trans>} />;
  }

  const psid = psidValidationEnv ? (
    !dataObject?.product?.psid || dataObject?.product?.psid === "invalid" ? (
      <span className=" flex gap-2">
        <Trans>invalid PSID (need to check)</Trans>
        <ExclamationCircleIcon className="text-red-400 w-5 h-5" />
      </span>
    ) : (
      dataObject?.product?.psid
    )
  ) : !dataObject?.product?.psid || dataObject?.product?.psid === "invalid" ? (
    "-"
  ) : (
    dataObject?.product?.psid
  );

  const typeOfProduct: Record<string, string> = {
    TOBACCO_PRODUCTS: t`Tobacco products`,
    ALCOHOLIC_PRODUCTS: t`Alcoholic beverages (except brewing products)`,
    BEER_PRODUCTS: t`Brewing products`,
    MEDICINAL_PRODUCTS: t`Medicinal products for medical use`,
    WATER_AND_COLD_DRINKS: t`Water and soft drinks`,
    APPLIANCES: t`Appliances`,
  };

  return (
    <>
      <div className="grid gap-x-8 grid-cols-1 sm:grid-cols-2 border-b sm:border sm:rounded-md sm:px-4">
        <div className="flex sm:col-span-2 flex-wrap gap-x-4 py-2 border-t sm:py-4 sm:border-t-0 sm:border-b ">
          <div className="text-gray-500 text-left">
            <Trans>Product ID:</Trans>
          </div>
          <div className="text-gray-90 text-left">{dataObject?.id}</div>
        </div>

        {process.env.REACT_APP_CUSTOMER !== "SKAPPSK" && (
          <div className="flex flex-wrap gap-x-4 py-2 border-t sm:py-4 sm:border-0">
            <div className="text-gray-500 text-left">
              <Trans>PSID:</Trans>
            </div>
            <div className="text-gray-90 text-left">{psid}</div>
          </div>
        )}
      </div>

      <div className="mt-6 sm:mt-8 grid gap-x-8 grid-cols-1 sm:grid-cols-1 sm:border sm:rounded-md sm:px-4">
        <div className="flex flex-wrap gap-x-4 py-2 border-t border-b sm:py-4 sm:border-t-0">
          <div className="flex gap-x-2 items-center text-gray-500 text-left w-60">
            <PencilIcon className="h-5 w-5 text-gray-500" />
            <Trans>Product name:</Trans>
          </div>
          <div className="text-gray-90 text-left font-medium">
            {dataObject?.product?.name}
          </div>
        </div>

        <div className="flex flex-wrap gap-x-4 py-2 border-b sm:py-4">
          <div className="flex gap-x-2 items-center text-gray-500 text-left w-60">
            <TagIcon className="h-5 w-5 text-gray-500" />
            <Trans>Unit:</Trans>
          </div>
          <div className="text-gray-90 text-left">{dataObject?.units}</div>
        </div>

        <div className="flex flex-wrap gap-x-4 py-2 border-b sm:py-4 ">
          <div className="flex gap-x-2 items-center text-gray-500 text-left w-60">
            <BookmarkIcon className="h-5 w-5 text-gray-500" />
            <Trans>Marked:</Trans>
          </div>
          <div className="text-gray-90 text-left">
            {dataObject?.product?.marked ? (
              <Trans>Yes</Trans>
            ) : (
              <Trans>No</Trans>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-x-4 py-2 border-b sm:py-4 ">
          <div className="flex gap-x-2 items-center text-gray-500 text-left w-60">
            <BookmarkIcon className="h-5 w-5 text-gray-500" />
            <Trans>Product Type:</Trans>
          </div>
          <div className="text-gray-90 text-left">
            {typeOfProduct[dataObject?.product?.productType] ?? "-"}
          </div>
        </div>

        {process.env.REACT_APP_CUSTOMER !== "SKAPPSK" && (
          <div className="flex flex-wrap gap-x-4 py-2 border-b sm:py-4">
            <div className="flex gap-x-2 items-center text-gray-500 text-left w-60">
              <ReceiptTaxIcon className="h-5 w-5 text-gray-500" />
              <Trans>VAT</Trans>:
            </div>
            <div className="text-gray-90 text-left">
              {dataObject?.vatRate ? (dataObject.vatRate * 100).toFixed(0) : 0}{" "}
              %
            </div>
          </div>
        )}

        <div className="flex flex-wrap gap-x-4 py-2 border-b sm:py-4 ">
          <div className="flex gap-x-2 items-center text-gray-500 text-left w-60">
            <BookmarkIcon className="h-5 w-5 text-gray-500" />
            <Trans>Quantity:</Trans>
          </div>
          <div className="text-gray-90 text-left">
            {dataObject?.amount ?? "-"}
          </div>
        </div>

        <div className="flex flex-wrap gap-x-4 py-2 border-b sm:py-4 ">
          <div className="flex gap-x-2 items-center text-gray-500 text-left w-60">
            <CashIcon className="h-5 w-5 text-gray-500" />
            <Trans>Price per unit:</Trans>
          </div>
          <div className="text-gray-90 text-left">
            {dataObject?.price ?? "-"}
          </div>
        </div>

        <div className="flex flex-wrap gap-x-4 py-2 border-b sm:py-4 ">
          <div className="flex gap-x-2 items-center text-gray-500 text-left w-60">
            <CashIcon className="h-5 w-5 text-gray-500" />
            <Trans>Purchase price: </Trans>
          </div>
          <div className="text-gray-90 text-left">
            {dataObject?.totalSum ?? "-"}
          </div>
        </div>
      </div>

      <div className="hidden sm:mt-8 mb-2 gap-x-2 items-center font-medium">
        <Trans>Suppliers</Trans>
      </div>

      <div className="hidden border-t border-b sm:border overflow-hidden sm:rounded-md py-2">
        <div className="flex flex-col divide-y">
          <div>
            <span className="flex-grow text-right p-5 text-md">-</span>
          </div>
        </div>
      </div>

      <div className="mt-6 sm:mt-8 mb-2 flex gap-x-2 items-center font-medium">
        <Trans>Location (Sales Address):</Trans>
      </div>

      <div className="border-t border-b sm:border overflow-hidden sm:rounded-md py-2">
        <span
          style={{ height: 30 }}
          className="block py-2 px-4 text-gray-400"
        />
      </div>

      {dataObject?.product?.barcodes?.length ? (
        <>
          <div className="mt-6 sm:mt-8 mb-2 flex gap-x-2 items-center font-medium">
            <Trans>Barcodes:</Trans>
          </div>
          <div className="sm:px-4 py-2 border-t border-b sm:border overflow-hidden sm:rounded-md">
            <div className="flex flex-wrap gap-x-4 gap-y-2">
              {dataObject?.product?.barcodes.map((item) => (
                <span key={item}>{item}</span>
              ))}
            </div>
          </div>
        </>
      ) : null}

      {dataObject?.markedLabels?.length ? (
        <>
          <div className="mt-6 sm:mt-8 mb-2 flex gap-x-2 items-center font-medium">
            <Trans>Marking codes:</Trans>
          </div>
          <div className="overflow-hidden">
            <div className="flex flex-col gap-x-4 gap-y-2">
              {dataObject.markedLabels.map((item) => (
                <span
                  style={{
                    color: "#6B7280",
                    padding: "9px 16px",
                    borderRadius: "6px",
                    background: "#F3F4F6",
                    border: "1px solid #E5E7EB",
                  }}
                  key={item.id}
                >
                  {item.label}
                </span>
              ))}
            </div>
          </div>
        </>
      ) : null}

      <div className="flex flex-wrap gap-x-4 gap-y-2 md:justify-end">
        <div className="flex gap-4 mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
          <Button
            type="button"
            secondary={true}
            label={<Trans>Close</Trans>}
            onClick={() => setOpenPopup(false)}
          />
        </div>
      </div>
    </>
  );
};
